import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  CreateOrUpdateInvestmentParams,
  Investment,
  Region,
} from "@bn/api/apiv1";
import { Account, Tag } from "@api";
import {
  IndustrySelector,
  RegionSelector,
  TagSelector,
  LoadingButton,
} from "@components";
import {
  EditTabContainer,
  EditTabFields,
  EditTabActions,
} from "./EditTabLayout";

interface Props {
  investment: Investment;
  depots: Account[];
  tags?: Tag[];
  isSaving: boolean;
  onSave: (data: CreateOrUpdateInvestmentParams) => void;
}

export const InvestmentEditBasicTabContainer = ({
  investment,
  depots = [],
  tags = [],
  isSaving = false,
  onSave,
}: Props) => {
  const { t } = useTranslation();
  const [name, setName] = useState(investment.name);
  const [depotId, setDepot] = useState(investment.account.id);
  const [industry, setIndustry] = useState(investment.industry ?? null);
  const [region, setRegion] = useState<Region | null>(
    (investment.region as Region) ?? null
  );
  const [customGroup, setCustomGroup] = useState(investment.tag?.value ?? null);

  const handleSave = () => {
    const data = {
      name,
      accountId: depotId,
      industryId: industry?.id ?? undefined,
      regionId: region?.id ?? undefined,
      tag: customGroup ?? "",
    };

    onSave(data);
  };

  const hasDepots = Boolean(depots.length);
  const convertedTags: string[] = tags
    .filter(({ value }) => Boolean(value))
    .map(({ value }) => value as string);

  return (
    <EditTabContainer>
      <EditTabFields>
        <TextField
          value={name}
          onChange={(e) => setName(e.target.value)}
          label={t("investmentEditPage.basicTab.name")}
          slotProps={{
            htmlInput: { "data-testid": `investment-name-input` },
          }}
        />
        {hasDepots && (
          <FormControl>
            <InputLabel id="depot-select-label">
              {t("investmentEditPage.basicTab.depot")}
            </InputLabel>
            <Select
              labelId="depot-select-label"
              fullWidth
              value={depotId}
              onChange={(event) => setDepot(event.target.value as number)}
            >
              {depots.map(({ name, id }) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <IndustrySelector
          defaultIndustry={investment.industry}
          onChange={(value) => setIndustry(value)}
        />
        <RegionSelector
          defaultRegion={investment.region}
          includeGlobal
          onChange={(value) => setRegion(value)}
        />
        <TagSelector
          defaultTag={investment.tag?.value}
          tags={convertedTags}
          onChange={(value) => setCustomGroup(value)}
        />
      </EditTabFields>
      <EditTabActions>
        <LoadingButton
          data-testid="investment-edit-save-button"
          type="submit"
          isFetching={isSaving}
          variant="primary"
          disabled={isSaving}
          onClick={handleSave}
        >
          {t("investmentEditPage.basicTab.saveButton")}
        </LoadingButton>
      </EditTabActions>
    </EditTabContainer>
  );
};
